<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        scrollable
        no-close-on-backdrop
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Additional Amount</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Name *"
                        label-for="name"
                        class="text-start mb-2"
                    >
                        <BFormInput
                            v-model="form.name"
                            type="text"
                            id="name"
                            placeholder="Enter Name"
                            :class="{
                                'is-invalid':
                                    v$.form.name.$dirty &&
                                    v$.form.name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.name.$dirty && v$.form.name.$invalid
                            "
                        >
                            {{ v$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>

            <!-- Foreign Currency Toggle -->
            <BRow v-if="currencyCode && currencyCode !== 'IDR'">
                <BCol>
                    <div class="mb-3 ml-1">
                        <BFormCheckbox v-model="isForeign">
                            Use <strong>{{ currencyCode }}</strong> (1 {{ currencyCode }} = {{ formatDecimal(exchangeRate) }} IDR)
                        </BFormCheckbox>
                    </div>
                </BCol>
            </BRow>

            <BRow>
                <BCol>
                    <BFormGroup
                        label="Pax *"
                        label-for="pax"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.pax"
                            type="number"
                            id="pax"
                            placeholder="Enter Pax"
                            @update:modelValue="calculateSubtotal"
                            :class="{
                                'is-invalid':
                                    v$.form.pax.$dirty && v$.form.pax.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.pax.$dirty && v$.form.pax.$invalid"
                        >
                            {{ v$.form.pax.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol>
                    <BFormGroup
                        :label="`Price per Pax (${isForeign ? currencyCode : 'IDR'}) *`"
                        label-for="price"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.price"
                            type="number"
                            id="price"
                             @update:modelValue="calculateSubtotal"
                            placeholder="Enter Price"
                            :class="{
                                'is-invalid':
                                    v$.form.price.$dirty &&
                                    v$.form.price.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.price.$dirty && v$.form.price.$invalid
                            "
                        >
                            {{ v$.form.price.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        :label="`Subtotal (${isForeign ? currencyCode : 'IDR'}) *`"
                        label-for="sub_total"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.sub_total"
                            type="number"
                            id="sub_total"
                            @update:modelValue="calculatePrice"
                            :class="{
                                'is-invalid':
                                    v$.form.sub_total.$dirty &&
                                    v$.form.sub_total.$invalid,
                            }"
                            placeholder="Subtotal"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.sub_total.$dirty &&
                                v$.form.sub_total.$invalid
                            "
                        >
                            {{ v$.form.sub_total.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Inclusion"
                        label-for="inclusion"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.inclusion"
                            id="inclusion"
                            placeholder="Enter Inclusion"
                            :rows="3"
                            :class="{
                                'is-invalid':
                                    v$.form.inclusion.$dirty &&
                                    v$.form.inclusion.$invalid,
                            }"
                        >
                        </BFormTextarea>
                        <BFormInvalidFeedback
                            :state="
                                v$.form.inclusion.$dirty &&
                                v$.form.inclusion.$invalid
                            "
                        >
                            {{ v$.form.inclusion.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Exclusion"
                        label-for="exclusion"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.exclusion"
                            id="exclusion"
                            placeholder="Enter Exclusion"
                            :rows="3"
                            :class="{
                                'is-invalid':
                                    v$.form.exclusion.$dirty &&
                                    v$.form.exclusion.$invalid,
                            }"
                        >
                        </BFormTextarea>
                        <BFormInvalidFeedback
                            :state="
                                v$.form.exclusion.$dirty &&
                                v$.form.exclusion.$invalid
                            "
                        >
                            {{ v$.form.exclusion.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch, ref } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormTextarea,
    BFormCheckbox,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue, minLength } from "@vuelidate/validators";
import { useLoading } from "vue-loading-overlay";
import { useToast } from "vue-toastification";
import { formatDecimal, parseRupiah } from "@/utils/format";

const $loading = useLoading();
const toast = useToast();

const isForeign = ref(false);

// Props
const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
    defaultPax: Number,
    exchangeRate: {
        type: Number,
        default: 1
    },
    currencyCode: {
        type: String,
        default: 'IDR'
    }
});

// Emit
const emit = defineEmits(["update:modal", "addOrUpdate"]);

// Function to update modal visibility
const updateModal = (value) => {
    emit("update:modal", value); // Sync modal state with parent
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

const formData = {
    uuid: "",
    name: "",
    pax: "",
    price: "",
    sub_total: "",
    currency: "IDR",
    inclusion: "",
    exclusion: "",
};

const form = reactive({ ...formData });

const rules = {
    form: {
        name: {
            required: helpers.withMessage("Name is required", required),
            minLength: helpers.withMessage(
                "Name must be at least 3",
                minLength(3)
            ),
        },
        pax: {
            required: helpers.withMessage("Pax is required", required),
            minValue: helpers.withMessage(
                "Pax must be positive and not zero",
                minValue(1)
            ),
        },
        price: {
            required: helpers.withMessage("Price is required", required),
            minValue: helpers.withMessage(
                "Price is must be at least 1",
                minValue(1)
            ),
        },
        sub_total: {
            required: helpers.withMessage("Subtotal is required", required),
            minValue: helpers.withMessage(
                "Subtotal is must be at least 1",
                minValue(1)
            ),
        },
        inclusion: {},
        exclusion: {},
    },
};

const v$ = useVuelidate(rules, { form }, { $scope: false });

// Init data jika update
watch(
    () => props.selectedData,
    (newData) => {
        // jika props.selectedData != null (action edit)
        if (newData && Object.keys(newData).length > 0) {
            const isItemForeign = newData.currency && newData.currency !== 'IDR' && newData.currency === props.currencyCode;
            const rate = props.exchangeRate || 1;

            let displayPrice = newData.price ? parseRupiah(newData.price) : 0;
            let displaySubTotal = newData.sub_total ? parseRupiah(newData.sub_total) : 0;

            if (isItemForeign) {
                displayPrice = Number((displayPrice / rate).toFixed(2));
                displaySubTotal = Number((displaySubTotal / rate).toFixed(2));
            }

            const data = {
                ...newData,
                price: displayPrice,
                sub_total: displaySubTotal,
                currency: newData.currency || 'IDR'
            };

            // Temporarily set isForeign to avoid triggering conversion in its own watcher
            if (isForeign.value !== isItemForeign) {
                skipConversion = true;
                isForeign.value = isItemForeign;
            }
            
            // Assign calculated display values to form
            Object.assign(form, { ...data });
        } else {
            Object.assign(form, { ...formData, pax: props.defaultPax });
            isForeign.value = false;
        }
        v$.value.$reset();
    },
    { immediate: true }
);

// Flag to prevent double conversion when isForeign is toggled programmatically
let skipConversion = false;

// Currency Switcher Watcher
watch(isForeign, (newVal) => {
    // If skipConversion is set, we just reset the flag and return
    if (skipConversion) {
        skipConversion = false;
        return;
    }

    const rate = props.exchangeRate || 1;
    if (newVal) {
        // Convert IDR -> Foreign
        form.price = Number((form.price / rate).toFixed(2));
        form.sub_total = Number((form.sub_total / rate).toFixed(2));
        form.currency = props.currencyCode;
    } else {
        // Convert Foreign -> IDR
        form.price = Math.round(form.price * rate);
        form.sub_total = Math.round(form.sub_total * rate);
        form.currency = 'IDR';
    }
});

const handleAction = () => {
    props.selectedData.index != undefined ? update() : create();
};

const preparePayload = () => {
    const payload = { ...form };
    const rate = props.exchangeRate || 1;
    
    // If in foreign mode, convert back to IDR before emitting
    if (isForeign.value) {
        payload.price = Math.round(payload.price * rate);
        payload.sub_total = Math.round(payload.sub_total * rate);
        payload.currency = props.currencyCode;
    } else {
        payload.currency = 'IDR';
    }
    
    return payload;
};

const create = async () => {
    toast.clear();
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });

        try {
            const payload = preparePayload();
            delete payload.index;
            emit("addOrUpdate", payload);
            toast.success("Additional added successfully");
            updateModal(false);
            resetForm();
        } finally {
            loader.hide();
        }
    }
};

const update = async () => {
    toast.clear();
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });

        try {
            const payload = preparePayload();
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...payload,
            });
            toast.success("Additional updated successfully");
            updateModal(false);
            resetForm();
        } finally {
            loader.hide();
        }
    }
};

watch(
    () => [form.pax, form.price],
    ([newPax, newPrice]) => {
        const pax = parseFloat(newPax) || 0;
        const price = parseFloat(newPrice) || 0;
        form.sub_total = isForeign.value ? Number((pax * price).toFixed(2)) : Math.round(pax * price);
    }
);

const calculateSubtotal = () => {
    const pax = parseFloat(form.pax) || 0;
    const price = parseFloat(form.price) || 0;
    form.sub_total = isForeign.value ? Number((pax * price).toFixed(2)) : Math.round(pax * price);
};

const calculatePrice = () => {
    const pax = parseFloat(form.pax) || 0;
    const subTotal = parseFloat(form.sub_total) || 0;
    
    if (pax !== 0) {
        form.price = isForeign.value ? Number((subTotal / pax).toFixed(2)) : Math.round(subTotal / pax);
    }
};
</script>
